import { Injector } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { AbstractToolHelper } from '../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { deleteIfDefined } from '../../state/render-state';
import { MouseCoordinateService } from '../../utils/mouse-coordinate.service';
import { MeasureToolState } from './measure-tool';
import { calculateFreePointDistance } from './measure-utils';

export class FreePointToolHelper extends AbstractToolHelper {
  private shellConnector: ShellConnector;
  private state = MeasureToolState.Waiting;
  private store: PointCloudContentStore;
  private mouseCoordinateService: MouseCoordinateService;

  constructor(injector: Injector) {
    super();
    this.shellConnector = injector.get(shellConnectorToken);
    this.store = injector.get(PointCloudContentStore);
    this.mouseCoordinateService = injector.get(MouseCoordinateService);
  }

  override startListening() {
    this.store('render')('measurementMode').set('free-point');
  }

  handleClick() {
    switch (this.state) {
      case MeasureToolState.Waiting:
        this.startMeasurement();
        break;
      case MeasureToolState.InProgress:
        this.endMeasurement();
        break;
      case MeasureToolState.Blocked:
        this.resetMeasure();
        break;
    }
  }

  protected override cleanUp() {
    super.cleanUp();
    this.store('render')('measurementMode').setUsing(deleteIfDefined);
    this.store('render')('freePointMeasurement').setUsing(deleteIfDefined);
  }

  private startMeasurement() {
    this.store('render')('freePointMeasurement').set({
      reference: this.mouseCoordinateService.getGroundedMousePosition(),
      active: true,
    });
    this.state = MeasureToolState.InProgress;
    this.trackEvent({ source: 'start_measure' });
  }

  private endMeasurement() {
    const measurement = this.store.state().render.freePointMeasurement;
    assert(isDefined(measurement), 'Measurement should be defined');
    this.store('render')('freePointMeasurement').assign({
      active: false,
    });
    const groundedImagePoint =
      this.mouseCoordinateService.getGroundedMousePosition();

    this.state = MeasureToolState.Blocked;
    this.trackEvent({
      source: 'end_measure',
      distance_measured: calculateFreePointDistance(
        this.store.state(),
        groundedImagePoint,
      ),
    });
  }

  private resetMeasure() {
    void this.stopListening();
    this.trackEvent({ source: 'reset_measure' });
  }

  private trackEvent(eventContext: EventTrackingValue) {
    this.shellConnector.trackEvent({
      id: 'measurement_click',
      bucket: 'navigation',
      sub_bucket: 'frame',
      mode: 'free-point',
      ...eventContext,
    });
  }
}
